import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  DollarSign, 
  FileText, 
  BarChart3, 
  Database,
  Calendar,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Lightbulb,
  Target,
  Zap,
  Clock,
  Eye,
  ChevronRight,
  ChevronDown,
  Download,
  Share2,
  Edit
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const SylvanFeed = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [expandedCards, setExpandedCards] = useState({
    1: true, // RFI expanded by default
    2: true, // Change Orders expanded by default
    3: true, // Submittals expanded by default
    4: true  // Schedule expanded by default
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState('25-111');
  const [realTimeData, setRealTimeData] = useState({});

  // Auto-fetch real-time data when project changes (only for 25-111)
  useEffect(() => {
    if (selectedProject === '25-111') {
      fetchRealTimeData(selectedProject);
    }
  }, [selectedProject]);

  // Demo data for Alex Cumming (PM for multiple projects)
  const feedData = {
    user: {
      name: "Alex Cumming",
      role: "Project Manager",
      projects: ["25-111", "23-119"],
      currentProject: selectedProject,
      avatar: "AC",
      lastActive: "2 hours ago"
    },
    projects: {
      "25-111": {
        queries: [
          {
            id: 1,
            title: "Most Recent RFI",
            category: "RFI",
            icon: FileText,
            color: "blue",
            priority: "high",
            lastUpdated: "2 hours ago",
            data: {
              total: 12,
              recent: 3,
              pending: 2,
              resolved: 10,
              avgResponseTime: "2.3 days",
              trend: "+15%"
            },
            recentItems: [
              { id: "RFI-2025-001", subject: "Electrical Panel Location", status: "Pending", date: "2025-01-15", assignee: "John Smith" },
              { id: "RFI-2025-002", subject: "HVAC Ductwork Routing", status: "Resolved", date: "2025-01-14", assignee: "Sarah Johnson" },
              { id: "RFI-2025-003", subject: "Structural Beam Details", status: "In Review", date: "2025-01-13", assignee: "Mike Davis" }
            ]
          },
          {
            id: 2,
            title: "Most Recent Change Orders",
            category: "Change Orders",
            icon: Edit,
            color: "orange",
            priority: "medium",
            lastUpdated: "4 hours ago",
            data: {
              total: 8,
              recent: 2,
              pending: 1,
              approved: 7,
              totalValue: "$45,200",
              trend: "+8%"
            },
            recentItems: [
              { id: "CO-2025-001", description: "Additional Electrical Outlets", value: "$2,500", status: "Approved", date: "2025-01-15" },
              { id: "CO-2025-002", description: "HVAC System Upgrade", value: "$12,800", status: "Pending", date: "2025-01-14" },
              { id: "CO-2025-003", description: "Flooring Material Change", value: "$8,900", status: "Approved", date: "2025-01-12" }
            ]
          },
          {
            id: 3,
            title: "Most Recent Submittals",
            category: "Submittals",
            icon: FileText,
            color: "green",
            priority: "medium",
            lastUpdated: "1 hour ago",
            data: {
              total: 24,
              recent: 5,
              pending: 3,
              approved: 21,
              avgReviewTime: "1.8 days",
              trend: "+12%"
            },
            recentItems: [
              { id: "SUB-2025-001", description: "Concrete Mix Design", status: "Approved", date: "2025-01-15", reviewer: "Engineering" },
              { id: "SUB-2025-002", description: "Steel Reinforcement Details", status: "Pending", date: "2025-01-14", reviewer: "Structural" },
              { id: "SUB-2025-003", description: "Paint Specifications", status: "In Review", date: "2025-01-13", reviewer: "Architecture" }
            ]
          },
          {
            id: 4,
            title: "Project Schedule Completion",
            category: "Schedule",
            icon: Calendar,
            color: "indigo",
            priority: "high",
            lastUpdated: "30 minutes ago",
            data: {
              overallCompletion: "78%",
              plannedCompletion: "82%",
              variance: "-4%",
              onTimeTasks: 24,
              delayedTasks: 6,
              criticalPath: "On Track",
              trend: "+3%"
            },
            recentItems: [
              { phase: "Foundation", completion: "100%", status: "Completed", plannedDate: "2025-01-10", actualDate: "2025-01-08", variance: "+2 days" },
              { phase: "Framing", completion: "85%", status: "In Progress", plannedDate: "2025-01-20", actualDate: "TBD", variance: "On Track" },
              { phase: "Electrical Rough-in", completion: "60%", status: "In Progress", plannedDate: "2025-01-25", actualDate: "TBD", variance: "On Track" }
            ]
          }
        ]
      },
      "23-119": {
        queries: [
          {
            id: 1,
            title: "Most Recent RFI",
            category: "RFI",
            icon: FileText,
            color: "blue",
            priority: "medium",
            lastUpdated: "1 hour ago",
            data: {
              total: 8,
              recent: 2,
              pending: 1,
              resolved: 7,
              avgResponseTime: "1.8 days",
              trend: "+22%"
            },
            recentItems: [
              { id: "RFI-2024-045", subject: "Foundation Waterproofing", status: "Resolved", date: "2025-01-15", assignee: "Lisa Chen" },
              { id: "RFI-2024-046", subject: "Window Installation Details", status: "Pending", date: "2025-01-14", assignee: "Tom Wilson" },
              { id: "RFI-2024-047", subject: "Roofing Material Specs", status: "In Review", date: "2025-01-13", assignee: "Sarah Johnson" }
            ]
          },
          {
            id: 2,
            title: "Most Recent Change Orders",
            category: "Change Orders",
            icon: Edit,
            color: "orange",
            priority: "low",
            lastUpdated: "3 hours ago",
            data: {
              total: 5,
              recent: 1,
              pending: 0,
              approved: 5,
              totalValue: "$28,500",
              trend: "+5%"
            },
            recentItems: [
              { id: "CO-2024-012", description: "Landscaping Enhancement", value: "$8,500", status: "Approved", date: "2025-01-14" },
              { id: "CO-2024-013", description: "Interior Finishing Upgrade", value: "$15,200", status: "Approved", date: "2025-01-12" },
              { id: "CO-2024-014", description: "Lighting Fixture Change", value: "$4,800", status: "Approved", date: "2025-01-10" }
            ]
          },
          {
            id: 3,
            title: "Most Recent Submittals",
            category: "Submittals",
            icon: FileText,
            color: "green",
            priority: "low",
            lastUpdated: "2 hours ago",
            data: {
              total: 18,
              recent: 3,
              pending: 1,
              approved: 17,
              avgReviewTime: "1.5 days",
              trend: "+18%"
            },
            recentItems: [
              { id: "SUB-2024-089", description: "Flooring Material Samples", status: "Approved", date: "2025-01-15", reviewer: "Architecture" },
              { id: "SUB-2024-090", description: "HVAC Equipment Specs", status: "Pending", date: "2025-01-14", reviewer: "MEP" },
              { id: "SUB-2024-091", description: "Door Hardware Details", status: "Approved", date: "2025-01-13", reviewer: "Architecture" }
            ]
          },
          {
            id: 4,
            title: "Project Schedule Completion",
            category: "Schedule",
            icon: Calendar,
            color: "indigo",
            priority: "medium",
            lastUpdated: "1 hour ago",
            data: {
              overallCompletion: "92%",
              plannedCompletion: "90%",
              variance: "+2%",
              onTimeTasks: 18,
              delayedTasks: 2,
              criticalPath: "Ahead of Schedule",
              trend: "+5%"
            },
            recentItems: [
              { phase: "Site Preparation", completion: "100%", status: "Completed", plannedDate: "2024-12-15", actualDate: "2024-12-12", variance: "+3 days" },
              { phase: "Building Construction", completion: "95%", status: "In Progress", plannedDate: "2025-01-30", actualDate: "TBD", variance: "Ahead of Schedule" },
              { phase: "Interior Finishing", completion: "85%", status: "In Progress", plannedDate: "2025-02-15", actualDate: "TBD", variance: "On Track" }
            ]
          }
        ]
      }
    },
    insights: {
      "25-111": [
        {
          id: 1,
          title: "RFI Response Time Optimization",
          type: "trend",
          period: "Last 180 days",
          insight: "RFI response times have improved by 23% over the past 6 months, with average response time dropping from 3.1 to 2.3 days. This improvement correlates with the implementation of the new digital RFI tracking system.",
          metrics: [
            { label: "Current Avg Response", value: "2.3 days", trend: "down", change: "-23%" },
            { label: "Resolution Rate", value: "83%", trend: "up", change: "+12%" },
            { label: "Pending RFIs", value: "2", trend: "down", change: "-60%" }
          ]
        },
        {
          id: 2,
          title: "Change Order Value Analysis",
          type: "financial",
          period: "Last 180 days",
          insight: "Change order values have increased by 8% this quarter, primarily driven by client-requested upgrades. The average change order value is $5,650, with 87% being approved within 5 business days.",
          metrics: [
            { label: "Total CO Value", value: "$45,200", trend: "up", change: "+8%" },
            { label: "Approval Rate", value: "87%", trend: "up", change: "+5%" },
            { label: "Avg Processing Time", value: "4.2 days", trend: "down", change: "-15%" }
          ]
        },
        {
          id: 3,
          title: "Schedule Recovery Analysis",
          type: "schedule",
          period: "Last 180 days",
          insight: "Project 25-111 is currently 4% behind schedule but showing positive momentum with recent task completions. Critical path analysis indicates potential for recovery with focused resource allocation.",
          metrics: [
            { label: "Overall Completion", value: "76%", trend: "up", change: "+4%" },
            { label: "On-Time Task Rate", value: "84%", trend: "up", change: "+8%" },
            { label: "Critical Path Health", value: "Good", trend: "up", change: "+12%" }
          ]
        }
      ],
      "23-119": [
        {
          id: 1,
          title: "Project Completion Excellence",
          type: "completion",
          period: "Last 180 days",
          insight: "Project 23-119 is performing exceptionally well with 98% completion and 12 days ahead of schedule. This represents one of the most successful project deliveries in recent history.",
          metrics: [
            { label: "Completion Rate", value: "98%", trend: "up", change: "+2%" },
            { label: "Days Ahead", value: "12 days", trend: "up", change: "+3 days" },
            { label: "Quality Score", value: "96%", trend: "up", change: "+5%" }
          ]
        },
        {
          id: 2,
          title: "Final Phase Efficiency",
          type: "process",
          period: "Last 180 days",
          insight: "The final phase of construction has been executed with remarkable efficiency. Punch list items are being resolved 40% faster than industry average, indicating excellent project management.",
          metrics: [
            { label: "Punch List Items", value: "12", trend: "down", change: "-75%" },
            { label: "Resolution Time", value: "1.2 days", trend: "down", change: "-40%" },
            { label: "Client Satisfaction", value: "98%", trend: "up", change: "+8%" }
          ]
        },
      ]
    },
    suggestedActions: {
      "25-111": {
        high: [
          {
            id: 1,
            title: "Follow up on Pending RFI-2025-001",
            category: "RFI",
            description: "Electrical Panel Location RFI has been pending for 2 days. Contact John Smith for status update.",
            estimatedTime: "15 minutes",
            impact: "Prevent potential delays in electrical rough-in"
          },
          {
            id: 8,
            title: "Accelerate Project Schedule",
            category: "Schedule",
            description: "Project 25-111 is 4% behind schedule. Review critical path and allocate additional resources to catch up.",
            estimatedTime: "45 minutes",
            impact: "Bring project back on schedule"
          }
        ],
        medium: [
          {
            id: 2,
            title: "Review Change Order CO-2025-002",
            category: "Change Orders",
            description: "HVAC System Upgrade change order ($12,800) requires client approval. Schedule meeting with client.",
            estimatedTime: "30 minutes",
            impact: "Secure additional project revenue"
          },
          {
            id: 3,
            title: "Expedite Submittal Review",
            category: "Submittals",
            description: "Steel Reinforcement Details submittal is pending review. Follow up with structural engineering team.",
            estimatedTime: "10 minutes",
            impact: "Maintain construction schedule"
          },
        ],
        low: [
          {
            id: 11,
            title: "Update Project Documentation",
            category: "Documentation",
            description: "Review and update project documentation to reflect recent changes and improvements.",
            estimatedTime: "30 minutes",
            impact: "Improve project knowledge management"
          }
        ]
      },
      "23-119": {
        high: [
          {
            id: 1,
            title: "Complete Final Walkthrough",
            category: "Quality",
            description: "Schedule final walkthrough with client to identify any remaining punch list items before project closeout.",
            estimatedTime: "60 minutes",
            impact: "Ensure client satisfaction and timely project closure"
          },
        ],
        medium: [
          {
            id: 3,
            title: "Resolve Remaining Punch List Items",
            category: "Quality",
            description: "Address the 12 remaining punch list items to achieve 100% project completion.",
            estimatedTime: "4 hours",
            impact: "Complete project to client specifications"
          },
          {
            id: 4,
            title: "Prepare Project Closeout Documentation",
            category: "Documentation",
            description: "Compile all project documentation, warranties, and as-built drawings for client delivery.",
            estimatedTime: "2 hours",
            impact: "Ensure complete project handover"
          },
          {
            id: 5,
            title: "Schedule Team Celebration",
            category: "Team Management",
            description: "Organize project completion celebration to recognize team's exceptional performance.",
            estimatedTime: "30 minutes",
            impact: "Boost team morale and recognition"
          }
        ],
        low: [
          {
            id: 6,
            title: "Update Project Portfolio",
            category: "Documentation",
            description: "Update project portfolio with lessons learned and best practices from this successful project.",
            estimatedTime: "45 minutes",
            impact: "Improve future project delivery"
          },
          {
            id: 7,
            title: "Request Client Testimonial",
            category: "Business Development",
            description: "Request client testimonial highlighting the successful project delivery and team performance.",
            estimatedTime: "15 minutes",
            impact: "Support business development efforts"
          }
        ]
      }
    }
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const handleRefresh = () => {
    if (selectedProject === '25-111') {
      fetchRealTimeData(selectedProject);
    } else {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setLastRefresh(new Date());
      }, 2000);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend.startsWith('+')) return <ArrowUpRight className="h-4 w-4 text-green-500" />;
    if (trend.startsWith('-')) return <ArrowDownRight className="h-4 w-4 text-red-500" />;
    return <TrendingUp className="h-4 w-4 text-gray-500" />;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'RFI': return <FileText className="h-5 w-5" />;
      case 'Change Orders': return <Edit className="h-5 w-5" />;
      case 'Submittals': return <FileText className="h-5 w-5" />;
      case 'Finance': return <DollarSign className="h-5 w-5" />;
      case 'Safety': return <AlertTriangle className="h-5 w-5" />;
      case 'Schedule': return <Calendar className="h-5 w-5" />;
      default: return <BarChart3 className="h-5 w-5" />;
    }
  };

  // Real-time data fetching functions
  const fetchRealTimeData = async (projectId) => {
    if (projectId !== '25-111') {
      return;
    }

    setIsLoading(true);
    try {
      const queries = [
        {
          id: '1', // Maps to query ID 1 (RFI)
          query: 'Most recent RFI items with item name, description, latest progress, insights and actions',
          category: 'RFI'
        },
        {
          id: '2', // Maps to query ID 2 (Change Orders)
          query: 'Most recent change order items with item name, description, latest progress, insights and actions',
          category: 'Change Orders'
        },
        {
          id: '3', // Maps to query ID 3 (Submittals)
          query: 'Most recent submittal items with item name, description, latest progress, insights and actions',
          category: 'Submittals'
        },
        {
          id: '4', // Maps to query ID 4 (Schedule)
          query: 'Most recent schedule completion progress with item name, description, latest progress, insights and actions',
          category: 'Schedule'
        }
      ];

      // Process queries individually and render results as they complete
      const results = {};
      
      // Create individual promises that update state immediately when complete
      const queryPromises = queries.map(async (queryItem) => {
        try {
          console.log(`Step 1: Searching for ${queryItem.category} with query: "${queryItem.query}"`);
          
          // Step 1: Search API
          const searchResponse = await axios.post('/api/25-111-search/search', {
            query: queryItem.query,
            top_k: 100
          });

          console.log(`Search response for ${queryItem.category}:`, searchResponse.data);

          if (searchResponse.data && searchResponse.data.success && searchResponse.data.results.length > 0) {
            console.log(`Step 2: Generating summary for ${queryItem.category} with ${searchResponse.data.results.length} results`);
            
            // Step 2: Summary API
            const summaryResponse = await axios.post('/api/25-111-search/summary', {
              query: queryItem.query,
              results: searchResponse.data.results
            });

            console.log(`Summary response for ${queryItem.category}:`, summaryResponse.data);

            if (summaryResponse.data && summaryResponse.data.success) {
              console.log(`Step 3: Converting summary to table format for ${queryItem.category}`);
              
              // Step 3: Parallel processing - Table conversion + AI Insights + Suggested Actions
              try {
                const [tableResponse, insightsResponse, actionsResponse] = await Promise.all([
                  // Table conversion
                  axios.post('/api/25-111-search/convert-to-table', {
                    query: queryItem.query,
                    summary: summaryResponse.data.summary,
                    category: queryItem.category
                  }),
                  // AI Insights generation
                  axios.post('/api/25-111-search/generate-insights', {
                    query: queryItem.query,
                    summary: summaryResponse.data.summary,
                    category: queryItem.category
                  }),
                  // Suggested Actions generation
                  axios.post('/api/25-111-search/generate-actions', {
                    query: queryItem.query,
                    summary: summaryResponse.data.summary,
                    category: queryItem.category
                  })
                ]);

                console.log(`Table conversion response for ${queryItem.category}:`, tableResponse.data);
                console.log(`Insights response for ${queryItem.category}:`, insightsResponse.data);
                console.log(`Actions response for ${queryItem.category}:`, actionsResponse.data);

                const result = {
                  id: queryItem.id,
                  data: searchResponse.data.results || [],
                  summary: summaryResponse.data.summary || '',
                  tableData: tableResponse.data?.tableData || null,
                  insights: insightsResponse.data?.insights || null,
                  actions: actionsResponse.data?.actions || null,
                  category: queryItem.category
                };

                // Update state immediately when this query completes
                setRealTimeData(prev => ({
                  ...prev,
                  [queryItem.id]: result
                }));

                return result;
              } catch (tableError) {
                console.warn(`Table conversion failed for ${queryItem.category}, using summary only:`, tableError);
                const result = {
                  id: queryItem.id,
                  data: searchResponse.data.results || [],
                  summary: summaryResponse.data.summary || '',
                  tableData: null,
                  category: queryItem.category,
                  tableError: tableError.message
                };

                // Update state immediately when this query completes
                setRealTimeData(prev => ({
                  ...prev,
                  [queryItem.id]: result
                }));

                return result;
              }
            } else {
              console.warn(`Summary generation failed for ${queryItem.category}:`, summaryResponse.data);
              const result = {
                id: queryItem.id,
                data: searchResponse.data.results || [],
                summary: '',
                category: queryItem.category,
                error: 'Summary generation failed'
              };

              // Update state immediately when this query completes
              setRealTimeData(prev => ({
                ...prev,
                [queryItem.id]: result
              }));

              return result;
            }
          } else {
            console.warn(`No search results for ${queryItem.category}:`, searchResponse.data);
            const result = {
              id: queryItem.id,
              data: [],
              summary: '',
              category: queryItem.category,
              error: 'No search results'
            };

            // Update state immediately when this query completes
            setRealTimeData(prev => ({
              ...prev,
              [queryItem.id]: result
            }));

            return result;
          }
        } catch (error) {
          console.error(`Error processing ${queryItem.category}:`, error);
          const result = {
            id: queryItem.id,
            data: [],
            summary: '',
            category: queryItem.category,
            error: error.message
          };

          // Update state immediately when this query completes
          setRealTimeData(prev => ({
            ...prev,
            [queryItem.id]: result
          }));

          return result;
        }
      });

      // Start all queries but don't wait for all to complete
      Promise.all(queryPromises).then(queryResults => {
        console.log('All queries completed');
        // Final state update to ensure all results are captured
        const finalResults = {};
        queryResults.forEach(result => {
          finalResults[result.id] = result;
        });
        setRealTimeData(finalResults);
      });

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching real-time data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform AI search results into feed format
  const transformSearchResults = (searchResults, category) => {
    if (!searchResults || !searchResults.data || searchResults.data.length === 0) {
      console.log('No search results to transform for category:', category);
      return null;
    }

    const results = searchResults.data;
    const summary = searchResults.summary || '';
    
    console.log('Transforming search results for', category, ':', { results, summary });

    // Parse summary for key metrics - AI summary should contain structured data
    const parseMetrics = (summary, results) => {
      const metrics = {};
      
      console.log('Parsing AI summary:', summary);
      
      // Try to extract structured data from AI summary
      // Look for patterns like "Total: 5", "Recent: 3", "Pending: 0", "Resolved: 5"
      const totalMatch = summary.match(/total[:\s]*(\d+)/i) || summary.match(/(\d+)\s*total/i);
      const recentMatch = summary.match(/recent[:\s]*(\d+)/i) || summary.match(/(\d+)\s*recent/i);
      const pendingMatch = summary.match(/pending[:\s]*(\d+)/i) || summary.match(/(\d+)\s*pending/i);
      const resolvedMatch = summary.match(/resolved[:\s]*(\d+)/i) || summary.match(/(\d+)\s*resolved/i);
      const approvedMatch = summary.match(/approved[:\s]*(\d+)/i) || summary.match(/(\d+)\s*approved/i);
      const closedMatch = summary.match(/closed[:\s]*(\d+)/i) || summary.match(/(\d+)\s*closed/i);
      
      // Set metrics with fallbacks
      metrics.total = totalMatch ? parseInt(totalMatch[1]) : results.length;
      metrics.recent = recentMatch ? parseInt(recentMatch[1]) : Math.min(3, results.length);
      metrics.pending = pendingMatch ? parseInt(pendingMatch[1]) : 0;
      
      // Calculate resolved/approved based on category
      if (category === 'RFI') {
        metrics.resolved = resolvedMatch ? parseInt(resolvedMatch[1]) : (metrics.total - metrics.pending);
      } else if (category === 'Change Orders' || category === 'Submittals') {
        metrics.approved = approvedMatch ? parseInt(approvedMatch[1]) : (metrics.total - metrics.pending);
        metrics.resolved = metrics.approved; // For display purposes
      } else if (category === 'Safety') {
        metrics.closed = closedMatch ? parseInt(closedMatch[1]) : (metrics.total - metrics.pending);
        metrics.resolved = metrics.closed; // For display purposes
      } else {
        metrics.resolved = metrics.total - metrics.pending;
      }
      
      // Add trend data
      metrics.trend = "+5%"; // Default trend
      
      console.log('Parsed metrics from AI summary:', metrics);
      return metrics;
    };

    const metrics = parseMetrics(summary, results);
    console.log('Parsed metrics:', metrics);

    // Transform results into recent items format
    const recentItems = results.slice(0, 3).map((item, index) => {
      const baseItem = {
        id: item.id || item.filename || `ITEM-${Date.now()}-${index}`,
        date: item.date || item.created_date || new Date().toISOString().split('T')[0],
        status: item.status || 'Unknown'
      };

      switch (category) {
        case 'RFI':
          return {
            ...baseItem,
            subject: item.subject || item.title || item.content?.substring(0, 50) || 'RFI Request',
            assignee: item.assignee || 'Unknown'
          };
        case 'Change Orders':
          return {
            ...baseItem,
            description: item.description || item.title || item.content?.substring(0, 50) || 'Change Order',
            value: item.value || item.amount || '$0',
            status: item.status || 'Pending'
          };
        case 'Submittals':
          return {
            ...baseItem,
            description: item.description || item.title || item.content?.substring(0, 50) || 'Submittal',
            reviewer: item.reviewer || 'Engineering',
            status: item.status || 'Pending'
          };
        case 'Safety':
          return {
            ...baseItem,
            description: item.description || item.title || item.content?.substring(0, 50) || 'Safety Event',
            severity: item.severity || 'Low',
            reporter: item.reporter || 'Unknown'
          };
        case 'Schedule':
          return {
            ...baseItem,
            milestone: item.milestone || item.title || 'Project Milestone',
            completion: item.completion || item.progress || '0%',
            status: item.status || 'In Progress'
          };
        default:
          return baseItem;
      }
    });

    console.log('Transformed recent items:', recentItems);

    return {
      metrics,
      recentItems,
      summary
    };
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-sylvan-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
              {feedData.user.avatar}
            </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{feedData.user.name}</h1>
                      <p className="text-sm text-blue-800 bg-blue-50 px-3 py-2 rounded-md italic mt-1">
                        This is a demonstration page with simulated data. All project information, metrics, and actions shown here are for showcase purposes only.
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500">Projects:</span>
                        <select
                          value={selectedProject}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-sylvan-500"
                        >
                          {feedData.user.projects.map((project) => (
                            <option key={project} value={project}>
                              {project}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-sm text-gray-500">Last active: {feedData.user.lastActive}</p>
                    </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="text-sm text-gray-500">
              Last updated: {lastRefresh.toLocaleTimeString()}
              {selectedProject === '25-111' && <span className="ml-2 text-green-600">• Live Data</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        {[
          { id: 'feed', name: 'Feed', icon: TrendingUp },
          { id: 'insights', name: 'AI Insights', icon: Lightbulb },
          { id: 'actions', name: 'Suggested Actions', icon: Target }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-sylvan-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <Icon className="mr-2 h-4 w-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Feed Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'feed' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {feedData.projects[selectedProject].queries.map((query) => {
              // Use real-time data for 25-111, demo data for others
              const realTimeQuery = selectedProject === '25-111' && realTimeData[query.id.toString()];
              console.log(`Processing query ${query.id} (${query.category}):`, { realTimeQuery, selectedProject });
              
              // For 25-111, show AI summary directly; for others, show demo data
              const displayData = realTimeQuery && realTimeQuery.summary ? {
                ...query,
                title: query.title,
                category: query.category,
                lastUpdated: "Live Data",
                realTime: true,
                aiSummary: realTimeQuery.summary,
                tableData: realTimeQuery.tableData,
                searchResults: realTimeQuery.data || []
              } : selectedProject === '25-111' ? {
                ...query,
                lastUpdated: "Loading...",
                realTime: false,
                loading: true
              } : query;
              
              console.log(`Final display data for ${query.category}:`, displayData);
              const Icon = displayData.icon;
              const isExpanded = expandedCards[displayData.id];
              
              return (
                <div key={displayData.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleCardExpansion(displayData.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg bg-${displayData.color}-100`}>
                          <Icon className={`h-6 w-6 text-${displayData.color}-600`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {displayData.title}
                            {displayData.realTime && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">LIVE</span>}
                          </h3>
                          <p className="text-sm text-gray-600">Last updated: {displayData.lastUpdated}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(displayData.priority)}`}>
                          {displayData.priority.toUpperCase()}
                        </span>
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4">
                      {displayData.aiSummary ? (
                        /* Table Data Display for 25-111 */
                        <div>
                          {/* Structured Table Data */}
                          {displayData.tableData ? (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">Project Data</h4>
                              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      {displayData.tableData.headers?.map((header, index) => (
                                        <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {header}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {displayData.tableData.rows?.map((row, rowIndex) => (
                                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        {row.map((cell, cellIndex) => (
                                          <td key={cellIndex} className="px-4 py-2 text-sm text-gray-900">
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            /* Fallback when table data is not available */
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
                              <p className="text-sm text-gray-600">Processing data...</p>
                            </div>
                          )}
                        </div>
                      ) : displayData.loading ? (
                        /* Loading State */
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
                          <p className="text-sm text-gray-600">Loading project data...</p>
                        </div>
                      ) : (
                        /* Demo Data Display for other projects */
                        <div>
                          {/* Data Summary */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            {Object.entries(displayData.data).slice(0, 4).map(([key, value]) => (
                              <div key={key} className="bg-gray-50 rounded-lg p-3">
                                <div className="text-xs text-gray-600 capitalize">
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </div>
                                <div className="text-sm font-semibold text-gray-900 flex items-center">
                                  {value}
                                  {key.includes('trend') && getTrendIcon(value)}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Recent Items */}
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Recent Items</h4>
                            <div className="space-y-2">
                              {displayData.recentItems.slice(0, 2).map((item, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <div className="flex items-center space-x-2">
                                    {getCategoryIcon(displayData.category)}
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900 truncate">{item.id || item.description}</div>
                                      <div className="text-xs text-gray-600 truncate">{item.subject || item.description}</div>
                                    </div>
                                  </div>
                                  <div className="text-right ml-2">
                                    <div className="text-xs font-medium text-gray-900">{item.status}</div>
                                    <div className="text-xs text-gray-500">{item.date}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {selectedProject === '25-111' ? (
              // Real-time insights for Project 25-111 only
              Object.values(realTimeData).length > 0 ? (
                Object.values(realTimeData).map((queryData) => (
                  queryData.insights && queryData.insights.length > 0 ? (
                    <div key={queryData.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-sylvan-100 rounded-lg">
                            <Lightbulb className="h-5 w-5 text-sylvan-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{queryData.category} Insights</h3>
                            <p className="text-sm text-gray-600">Live Data</p>
                          </div>
                        </div>
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">LIVE</span>
                      </div>

                      <div className="space-y-4">
                        {queryData.insights.map((insight, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">{insight.title}</h4>
                            <p className="text-sm text-gray-700">{insight.description}</p>
                            {insight.type && (
                              <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                                insight.type === 'trend' ? 'bg-blue-100 text-blue-800' :
                                insight.type === 'pattern' ? 'bg-purple-100 text-purple-800' :
                                insight.type === 'warning' ? 'bg-red-100 text-red-800' :
                                insight.type === 'opportunity' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {insight.type.toUpperCase()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null
                )).filter(Boolean)
              ) : (
                <div className="col-span-2 text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Loading insights...</p>
                </div>
              )
            ) : (
              // Demo insights for other projects
              feedData.insights[selectedProject].map((insight) => (
                <div key={insight.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-sylvan-100 rounded-lg">
                        <Lightbulb className="h-5 w-5 text-sylvan-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{insight.title}</h3>
                        <p className="text-sm text-gray-600">{insight.period}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4 text-sm">{insight.insight}</p>

                  <div className="grid grid-cols-1 gap-3">
                    {insight.metrics.map((metric, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600">{metric.label}</div>
                        <div className="text-sm font-semibold text-gray-900 flex items-center">
                          {metric.value}
                          {getTrendIcon(metric.change)}
                          <span className={`ml-2 text-xs ${
                            metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {metric.change}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="space-y-8">
            {selectedProject === '25-111' ? (
              // Real-time actions for Project 25-111 only
              Object.values(realTimeData).length > 0 ? (
                (() => {
                  // Collect all actions from all queries and group by priority
                  const allActions = [];
                  Object.values(realTimeData).forEach(queryData => {
                    if (queryData.actions && queryData.actions.length > 0) {
                      queryData.actions.forEach(action => {
                        allActions.push({
                          ...action,
                          category: queryData.category,
                          queryId: queryData.id
                        });
                      });
                    }
                  });

                  // Group actions by priority
                  const highPriority = allActions.filter(action => action.priority === 'high');
                  const mediumPriority = allActions.filter(action => action.priority === 'medium');
                  const lowPriority = allActions.filter(action => action.priority === 'low');

                  return (
                    <>
                      {/* HIGH PRIORITY ACTIONS */}
                      {highPriority.length > 0 && (
                        <div>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-lg">
                              <Target className="h-5 w-5 text-red-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">HIGH PRIORITY</h2>
                            <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                              {highPriority.length} Actions
                            </span>
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">LIVE</span>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {highPriority.map((action, index) => (
                              <div key={`${action.queryId}-${index}`} className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start space-x-4">
                                    <div className="p-2 bg-red-100 rounded-lg">
                                      <Target className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                                      <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                                      <p className="text-gray-700 mt-1">{action.description}</p>
                                      <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                        <span className="flex items-center">
                                          <Clock className="h-4 w-4 mr-1" />
                                          {action.estimatedTime || 'N/A'}
                                        </span>
                                        <span className="flex items-center">
                                          <Zap className="h-4 w-4 mr-1" />
                                          {action.impact || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors">
                                      Take Action
                                    </button>
                                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                      <Share2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* MEDIUM PRIORITY ACTIONS */}
                      {mediumPriority.length > 0 && (
                        <div>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                              <Target className="h-5 w-5 text-yellow-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">MEDIUM PRIORITY</h2>
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                              {mediumPriority.length} Actions
                            </span>
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">LIVE</span>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {mediumPriority.map((action, index) => (
                              <div key={`${action.queryId}-${index}`} className="bg-white rounded-lg shadow-sm border border-yellow-200 p-6">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start space-x-4">
                                    <div className="p-2 bg-yellow-100 rounded-lg">
                                      <Target className="h-5 w-5 text-yellow-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                                      <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                                      <p className="text-gray-700 mt-1">{action.description}</p>
                                      <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                        <span className="flex items-center">
                                          <Clock className="h-4 w-4 mr-1" />
                                          {action.estimatedTime || 'N/A'}
                                        </span>
                                        <span className="flex items-center">
                                          <Zap className="h-4 w-4 mr-1" />
                                          {action.impact || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition-colors">
                                      Take Action
                                    </button>
                                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                      <Share2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* LOW PRIORITY ACTIONS */}
                      {lowPriority.length > 0 && (
                        <div>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Target className="h-5 w-5 text-green-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">LOW PRIORITY</h2>
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                              {lowPriority.length} Actions
                            </span>
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">LIVE</span>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {lowPriority.map((action, index) => (
                              <div key={`${action.queryId}-${index}`} className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start space-x-4">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                      <Target className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                                      <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                                      <p className="text-gray-700 mt-1">{action.description}</p>
                                      <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                        <span className="flex items-center">
                                          <Clock className="h-4 w-4 mr-1" />
                                          {action.estimatedTime || 'N/A'}
                                        </span>
                                        <span className="flex items-center">
                                          <Zap className="h-4 w-4 mr-1" />
                                          {action.impact || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors">
                                      Take Action
                                    </button>
                                    <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                      <Share2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {allActions.length === 0 && (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
                          <p className="text-sm text-gray-600">Loading actions...</p>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Loading actions...</p>
                </div>
              )
            ) : (
              // Demo actions for other projects
              <>
                {/* HIGH PRIORITY ACTIONS */}
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Target className="h-5 w-5 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">HIGH PRIORITY</h2>
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                      {feedData.suggestedActions[selectedProject].high.length} Actions
                    </span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {feedData.suggestedActions[selectedProject].high.map((action) => (
                      <div key={action.id} className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="p-2 bg-red-100 rounded-lg">
                              <Target className="h-5 w-5 text-red-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                              <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                              <p className="text-gray-700 mt-1">{action.description}</p>
                              <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {action.estimatedTime}
                                </span>
                                <span className="flex items-center">
                                  <Zap className="h-4 w-4 mr-1" />
                                  {action.impact}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors">
                              Take Action
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                              <Share2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* MEDIUM PRIORITY ACTIONS */}
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Target className="h-5 w-5 text-yellow-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">MEDIUM PRIORITY</h2>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                      {feedData.suggestedActions[selectedProject].medium.length} Actions
                    </span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {feedData.suggestedActions[selectedProject].medium.map((action) => (
                      <div key={action.id} className="bg-white rounded-lg shadow-sm border border-yellow-200 p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                              <Target className="h-5 w-5 text-yellow-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                              <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                              <p className="text-gray-700 mt-1">{action.description}</p>
                              <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {action.estimatedTime}
                                </span>
                                <span className="flex items-center">
                                  <Zap className="h-4 w-4 mr-1" />
                                  {action.impact}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition-colors">
                              Take Action
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                              <Share2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* LOW PRIORITY ACTIONS */}
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Target className="h-5 w-5 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">LOW PRIORITY</h2>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      {feedData.suggestedActions[selectedProject].low.length} Actions
                    </span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {feedData.suggestedActions[selectedProject].low.map((action) => (
                      <div key={action.id} className="bg-white rounded-lg shadow-sm border border-green-200 p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Target className="h-5 w-5 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
                              <p className="text-sm text-gray-600 mb-2">{action.category}</p>
                              <p className="text-gray-700 mt-1">{action.description}</p>
                              <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {action.estimatedTime}
                                </span>
                                <span className="flex items-center">
                                  <Zap className="h-4 w-4 mr-1" />
                                  {action.impact}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors">
                              Take Action
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                              <Share2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default SylvanFeed;
